import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { ImageUploadField } from '../components/ImageUploadField';
import { productService } from '../services/productService';
import { fieldClass, labelClass, slugify } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import type { Category } from '../types';

type CategoryForm = {
  name: string;
  slug: string;
  description: string;
  image: string;
  coverImage: string;
  isActive: boolean;
  order: number;
  parentId: string;
};

const emptyForm = (): CategoryForm => ({
  name: '',
  slug: '',
  description: '',
  image: '',
  coverImage: '',
  isActive: true,
  order: 0,
  parentId: '',
});

function categoryToForm(c: Category): CategoryForm {
  return {
    name: c.name ?? '',
    slug: c.slug ?? '',
    description: c.description ?? '',
    image: c.image ?? '',
    coverImage: c.coverImage ?? '',
    isActive: Boolean(c.isActive),
    order: Number(c.order) || 0,
    parentId: c.parent?.id ? String(c.parent.id) : '',
  };
}

function buildPayload(form: CategoryForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    slug: form.slug.trim() || slugify(form.name),
    description: form.description.trim(),
    image: form.image.trim(),
    coverImage: form.coverImage.trim() || form.image.trim(),
    isActive: form.isActive,
    order: Number(form.order) || 0,
  };
  if (form.parentId) {
    payload.parentId = form.parentId;
  }
  return payload;
}

export function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'delete' | null>(null);
  const [selected, setSelected] = useState<Category | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm());
  const [slugManual, setSlugManual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productService.getCategories();
      setCategories(data ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت دسته‌بندی‌ها');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeModal = () => {
    setModalMode(null);
    setSelected(null);
    setForm(emptyForm());
    setSlugManual(false);
  };

  const openCreate = () => {
    setSelected(null);
    setForm(emptyForm());
    setSlugManual(false);
    setModalMode('create');
  };

  const openEdit = (category: Category) => {
    setSelected(category);
    setForm(categoryToForm(category));
    setSlugManual(true);
    setModalMode('edit');
  };

  const openDelete = (category: Category) => {
    setSelected(category);
    setModalMode('delete');
  };

  const setField = <K extends keyof CategoryForm>(key: K, value: CategoryForm[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'name' && !slugManual) {
        next.slug = slugify(String(value));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('نام دسته الزامی است');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (modalMode === 'create') {
        await productService.createCategory(payload);
        toast.success('دسته با موفقیت ایجاد شد');
      } else if (modalMode === 'edit' && selected) {
        await productService.updateCategory(selected.id, payload);
        toast.success('دسته به‌روزرسانی شد');
      }
      closeModal();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await productService.deleteCategory(selected.id);
      toast.success('دسته حذف شد');
      closeModal();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = categories.filter((c) => c.id !== selected?.id);

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">دسته‌بندی‌ها</h1>
          <p className="text-slate-600 mt-1">مدیریت دسته‌بندی‌های محصولات</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن دسته
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عکس</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">کاور</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">نام دسته</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">Slug</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">زیردسته‌ها</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                    دسته‌بندی‌ای یافت نشد
                  </td>
                </tr>
              ) : (
                categories.map((category, index) => (
                  <tr
                    key={category.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm tabular-nums text-slate-500">{rowNumber(index)}</td>
                    <td className="px-6 py-4">
                      {category.image ? (
                        <img
                          src={category.image}
                          alt=""
                          className="h-12 w-12 rounded-lg object-cover border border-slate-200"
                        />
                      ) : (
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-[10px] text-slate-400">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {category.coverImage || category.image ? (
                        <img
                          src={category.coverImage || category.image}
                          alt=""
                          className="h-12 w-20 rounded-lg object-cover border border-slate-200"
                        />
                      ) : (
                        <span className="inline-flex h-12 w-20 items-center justify-center rounded-lg bg-slate-100 text-[10px] text-slate-400">
                          بدون کاور
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{category.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{category.slug}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {category.subcategories?.length ?? 0} زیردسته
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                          category.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {category.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-700 p-1"
                          onClick={() => openEdit(category)}
                          title="ویرایش"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 p-1"
                          onClick={() => openDelete(category)}
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'افزودن دسته' : 'ویرایش دسته'}
        footer={
          <>
            <button
              type="button"
              onClick={closeModal}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div>
            <label className={labelClass}>نام</label>
            <input
              className={fieldClass}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="نام دسته‌بندی"
            />
          </div>
          <div>
            <label className={labelClass}>Slug</label>
            <input
              className={fieldClass}
              value={form.slug}
              onChange={(e) => {
                setSlugManual(true);
                setField('slug', e.target.value);
              }}
              placeholder="slug-auto"
              dir="ltr"
            />
          </div>
          <div>
            <label className={labelClass}>توضیحات</label>
            <textarea
              className={fieldClass}
              rows={3}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="توضیحات دسته"
            />
          </div>
          <ImageUploadField
            label="عکس دسته (لیست و حباب‌ها)"
            value={form.image}
            onChange={(url) => setField('image', url)}
            hint="برای نمایش کوچک در منو و ردیف دسته‌ها."
          />
          <ImageUploadField
            label="کاور صفحه دسته"
            value={form.coverImage}
            onChange={(url) => setField('coverImage', url)}
            hint="تصویر عریض بالای صفحه دسته‌بندی. اگر خالی باشد از عکس دسته استفاده می‌شود."
          />
          <div>
            <label className={labelClass}>ترتیب</label>
            <input
              type="number"
              className={fieldClass}
              value={form.order}
              onChange={(e) => setField('order', Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <label className={labelClass}>دسته والد (اختیاری)</label>
            <select
              className={fieldClass}
              value={form.parentId}
              onChange={(e) => setField('parentId', e.target.value)}
            >
              <option value="">بدون والد</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            فعال
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="حذف دسته"
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={closeModal}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'در حال حذف...' : 'حذف'}
            </button>
          </>
        }
      >
        <p className="text-slate-700" dir="rtl">
          آیا از حذف دسته «{selected?.name}» مطمئن هستید؟ این عمل قابل بازگشت نیست.
        </p>
      </Modal>
    </div>
  );
}
